import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/i18n";
import { api, fileUrl } from "@/lib/api";

const HEX = /^#[0-9A-Fa-f]{6}$/;

export function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs text-neutral-400">{label}</Label>{children}</div>;
}

export function Pick({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testid}-${v}`}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Toggle({ label, hint, checked, onChange, testid, disabled }) {
  return (
    <label className="flex items-start justify-between gap-3 cursor-pointer">
      <span><span className="text-sm">{label}</span>{hint && <span className="block text-[11px] text-neutral-500 mt-0.5">{hint}</span>}</span>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}

export function BgPicker({ value, onChange, presets, idPrefix = "bg" }) {
  const { t, lang } = useI18n();
  const [hex, setHex] = useState(value);
  useEffect(() => setHex(value), [value]);
  return (
    <Field label={t("tool.background")}>
      <div className="flex flex-wrap gap-2" data-testid={`${idPrefix}-presets`}>
        {presets.map((p) => (
          <button key={p.hex} type="button" title={`${p[`name_${lang}`] || p.name_en} ${p.hex}`} onClick={() => onChange(p.hex)}
            data-testid={`${idPrefix}-preset-${p.hex.slice(1)}`}
            className={`h-9 w-9 rounded-lg border-2 transition-transform hover:scale-105 ${value.toUpperCase() === p.hex.toUpperCase() ? "border-emerald-500" : "border-[var(--line)]"}`}
            style={{ background: p.hex }} />
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input type="color" value={HEX.test(hex) ? hex : "#ffffff"} onChange={(e) => { setHex(e.target.value.toUpperCase()); onChange(e.target.value.toUpperCase()); }}
          className="h-9 w-12 rounded-md bg-transparent border border-[var(--line)] cursor-pointer" data-testid={`${idPrefix}-color-picker`} aria-label={t("tool.custom")} />
        <Input value={hex} maxLength={7} onChange={(e) => { const v = e.target.value.startsWith("#") ? e.target.value : `#${e.target.value}`; setHex(v.toUpperCase()); if (HEX.test(v)) onChange(v.toUpperCase()); }}
          className={`font-mono ${HEX.test(hex) ? "" : "border-red-500"}`} placeholder="#FFFFFF" data-testid={`${idPrefix}-hex-input`} aria-label={t("tool.hex")} />
      </div>
    </Field>
  );
}

export function SizePicker({ value, onChange, sizes }) {
  const { t } = useI18n();
  return (
    <Field label={t("tool.size")}>
      <div className="grid grid-cols-3 gap-2" data-testid="size-picker">
        {sizes.map((s) => (
          <button key={s.key} type="button" onClick={() => onChange(s.key)} data-testid={`size-${s.key.replace(":", "x")}`}
            className={`rounded-lg border px-2 py-2 text-xs transition-colors ${value === s.key ? "border-emerald-500 bg-emerald-500/10" : "border-[var(--line)] hover:border-neutral-500"}`}>
            <span className="font-semibold block">{s.key}</span><span className="text-neutral-500">{s.w}×{s.h}</span>
          </button>
        ))}
      </div>
    </Field>
  );
}

const optList = (t, group, keys) => keys.map((k) => [k, t(`opts.${group}.${k}`)]);

export function useModels() {
  const [models, setModels] = useState([]);
  useEffect(() => { api.get("/models").then((r) => setModels(r.data.items)); }, []);
  return models;
}

function ModelSelect({ value, onChange, allowNone }) {
  const { t } = useI18n();
  const models = useModels();
  if (!models.length) return <p className="text-xs text-neutral-500">{t("tool.noModels")} <Link to="/app/models" className="text-emerald-400">{t("tool.addModel")}</Link></p>;
  return (
    <div className="grid grid-cols-4 gap-2" data-testid="model-select">
      {allowNone && <button type="button" onClick={() => onChange(null)} className={`aspect-square rounded-lg border text-[11px] ${!value ? "border-emerald-500" : "border-[var(--line)]"}`} data-testid="model-option-none">{t("tool.noModel")}</button>}
      {models.map((m) => (
        <button key={m.id} type="button" onClick={() => onChange(m.id)} title={m.name} data-testid={`model-option-${m.id}`}
          className={`aspect-square rounded-lg overflow-hidden border-2 ${value === m.id ? "border-emerald-500" : "border-transparent"}`}>
          <img src={fileUrl(m.thumb_file_id)} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
        </button>
      ))}
    </div>
  );
}

function ShotType({ value, onChange }) {
  const { t } = useI18n();
  return (
    <Field label={t("mk.label")}>
      <div className="grid grid-cols-2 rounded-full border border-[var(--line)] bg-[var(--bg-2)] p-1" role="radiogroup" data-testid="shot-type-toggle">
        {[[false, "without"], [true, "with"]].map(([v, k]) => (
          <button key={k} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)} data-testid={`shot-type-${k}`}
            className={`rounded-full py-1.5 text-sm font-medium transition-colors ${value === v ? "bg-emerald-600 text-white" : "text-neutral-300 hover:text-white"}`}>{t(`mk.${k}`)}</button>
        ))}
      </div>
      <p className="text-[11px] text-neutral-500" data-testid="shot-type-hint">{t(value ? "mk.withHint" : "mk.withoutHint")}</p>
    </Field>
  );
}

export function HeroOptions({ s, set, config }) {
  const { t } = useI18n();
  const mk = !!s.with_model;
  return (
    <>
      <ShotType value={mk} onChange={(v) => set(v ? { with_model: true, ai_enhance: true } : { with_model: false })} />
      <BgPicker value={s.bg_hex} onChange={(v) => set({ bg_hex: v })} presets={config.bg_presets} />
      <SizePicker value={s.size} onChange={(v) => set({ size: v })} sizes={config.sizes} />
      <Field label={t("tool.lighting")}><Pick value={s.lighting} onChange={(v) => set({ lighting: v })} options={optList(t, "lighting", ["soft", "bright", "dramatic", "warm"])} testid="lighting-select" /></Field>
      <Toggle label={t("tool.aiEnhance")} hint={mk ? t("mk.heroAiNote") : t("tool.aiEnhanceHint")} checked={mk || s.ai_enhance} disabled={mk} onChange={(v) => set({ ai_enhance: v })} testid="ai-enhance-switch" />
      <Toggle label={t("tool.shadow")} checked={s.shadow} onChange={(v) => set({ shadow: v })} testid="shadow-switch" />
    </>
  );
}

export function ModelOptions({ s, set, config }) {
  const { t } = useI18n();
  const [src, setSrc] = useState(s.model_id ? "library" : "preset");
  return (
    <>
      <Field label={t("tool.category")}><Pick value={s.category} onChange={(v) => set({ category: v })} options={optList(t, "category", ["necklace", "earrings", "bracelet", "ring", "anklet", "watch", "sunglasses", "bag", "apparel", "other"])} testid="category-select" /></Field>
      <Field label={t("tool.modelSource")}>
        <div className="grid grid-cols-2 gap-2">
          {["preset", "library"].map((k) => (
            <button key={k} type="button" data-testid={`model-source-${k}`} onClick={() => {
              setSrc(k);
              if (k === "preset") set({ model_id: null, ...(s.framing === "reference" ? { framing: "close" } : {}) });
              else if (src !== "library") set({ framing: "reference" });
            }}
              className={`rounded-lg border py-2 text-sm ${src === k ? "border-emerald-500 bg-emerald-500/10" : "border-[var(--line)]"}`}>{t(`tool.${k}`)}</button>
          ))}
        </div>
      </Field>
      {src === "preset" ? (
        <Pick value={s.model_preset} onChange={(v) => set({ model_preset: v })} options={optList(t, "preset", ["female_natural", "female_editorial", "male_classic", "male_casual"])} testid="model-preset-select" />
      ) : (
        <ModelSelect value={s.model_id} onChange={(v) => set({ model_id: v })} />
      )}
      <Field label={t("tool.framing")}><Pick value={s.framing} onChange={(v) => set({ framing: v })} options={[...(src === "library" ? [["reference", t("tool.asReference")]] : []), ["close", t("tool.close")], ["wide", t("tool.wide")]]} testid="framing-select" /></Field>
      {src === "library" && <p className="text-[11px] text-neutral-500 -mt-2" data-testid="framing-pose-note">{t("tool.poseNote")}</p>}
      <Field label={t("tool.lighting")}><Pick value={s.lighting} onChange={(v) => set({ lighting: v })} options={optList(t, "lighting", ["soft", "natural", "dramatic", "warm", "bright"])} testid="lighting-select" /></Field>
      <Field label={t("tool.bgText")}><Input value={s.background} maxLength={200} placeholder={t("tool.bgPlaceholder")} onChange={(e) => set({ background: e.target.value })} data-testid="model-bg-input" /></Field>
      <SizePicker value={s.size} onChange={(v) => set({ size: v })} sizes={config.sizes} />
    </>
  );
}

export function LifestyleOptions({ s, set, config }) {
  const { t } = useI18n();
  return (
    <>
      <ShotType value={!!s.with_model} onChange={(v) => set(v ? { with_model: true } : { with_model: false, model_id: null })} />
      <Field label={t("tool.style")}>
        <div className="grid grid-cols-2 gap-2" data-testid="style-picker">
          {config.styles.map((k) => (
            <button key={k} type="button" onClick={() => set({ style: k })} data-testid={`style-${k}`}
              className={`rounded-lg border py-2 text-sm ${s.style === k ? "border-emerald-500 bg-emerald-500/10" : "border-[var(--line)] hover:border-neutral-500"}`}>{t(`opts.style.${k}`) || k}</button>
          ))}
        </div>
      </Field>
      <Field label={t("tool.mood")}><Pick value={s.mood} onChange={(v) => set({ mood: v })} options={optList(t, "mood", ["calm", "elegant", "vibrant", "moody", "warm"])} testid="mood-select" /></Field>
      <Field label={t("tool.lighting")}><Pick value={s.lighting} onChange={(v) => set({ lighting: v })} options={optList(t, "lighting", ["natural", "soft", "dramatic", "warm", "bright"])} testid="lighting-select" /></Field>
      <Field label={t("tool.sceneNotes")}><Textarea rows={2} maxLength={300} value={s.scene_notes} onChange={(e) => set({ scene_notes: e.target.value })} data-testid="scene-notes-input" /></Field>
      {s.with_model && <Field label={t("tool.optionalModel")}><ModelSelect value={s.model_id} onChange={(v) => set({ model_id: v })} allowNone /></Field>}
      <SizePicker value={s.size} onChange={(v) => set({ size: v })} sizes={config.sizes} />
    </>
  );
}

const DIM_KEYS = ["width", "height", "length", "diameter", "thickness", "chain_length", "custom"];

export function DimensionsOptions({ s, set, config }) {
  const { t } = useI18n();
  const dims = s.dims;
  const upd = (i, patch) => set({ dims: dims.map((d, j) => (j === i ? { ...d, ...patch } : d)) });
  return (
    <>
      <Field label={t("tool.measurements")}>
        <div className="space-y-2" data-testid="dims-list">
          {dims.map((d, i) => (
            <div key={i} className="grid grid-cols-[1fr_88px_auto] gap-2 items-center">
              <div className="space-y-1">
                <Pick value={d.key} onChange={(v) => upd(i, { key: v })} options={optList(t, "dim", DIM_KEYS)} testid={`dim-key-${i}`} />
                {d.key === "custom" && <Input placeholder={t("tool.customLabel")} maxLength={40} value={d.label || ""} onChange={(e) => upd(i, { label: e.target.value })} data-testid={`dim-label-${i}`} />}
              </div>
              <Input inputMode="decimal" placeholder={t("tool.value")} value={d.value} onChange={(e) => upd(i, { value: e.target.value.replace(/[^0-9.,]/g, "") })} data-testid={`dim-value-${i}`} />
              <button type="button" onClick={() => set({ dims: dims.filter((_, j) => j !== i) })} className="p-2 text-neutral-500 hover:text-white" data-testid={`dim-remove-${i}`}><X size={16} /></button>
            </div>
          ))}
          {dims.length < 8 && (
            <button type="button" onClick={() => set({ dims: [...dims, { key: "length", value: "" }] })} className="text-sm text-emerald-400 flex items-center gap-1" data-testid="dim-add-btn"><Plus size={15} />{t("tool.addMeasure")}</button>
          )}
          <p className="text-[11px] text-neutral-500">{t("tool.measureNote")}</p>
        </div>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("tool.unit")}><Pick value={s.unit} onChange={(v) => set({ unit: v })} options={[["mm", "mm"], ["cm", "cm"], ["in", "in"]]} testid="unit-select" /></Field>
        <Field label={t("tool.labelLang")}><Pick value={s.label_lang} onChange={(v) => set({ label_lang: v })} options={[["tr", "Türkçe"], ["en", "English"], ["de", "Deutsch"]]} testid="label-lang-select" /></Field>
      </div>
      <Field label={`${t("tool.infoTitle")} (${t("common.optional")})`}><Input maxLength={60} value={s.title} onChange={(e) => set({ title: e.target.value })} data-testid="info-title-input" /></Field>
      <BgPicker value={s.bg_hex} onChange={(v) => set({ bg_hex: v })} presets={config.bg_presets} />
      <SizePicker value={s.size} onChange={(v) => set({ size: v })} sizes={config.sizes} />
      <Toggle label={t("tool.removeBg")} checked={s.remove_bg} onChange={(v) => set({ remove_bg: v })} testid="remove-bg-switch" />
    </>
  );
}
